import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useToast, humanizeError } from '../shared/Toast'

const BLANK = { name: '', specialty: '', clinic_or_agency: '', phone: '', email: '', notes: '' }

export default function SpecialistsSection({ recipientId, canManage }) {
  const toast = useToast()
  const [list, setList] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null) // id | 'new' | null
  const [form, setForm] = useState(BLANK)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from('care_specialists')
      .select('*')
      .eq('care_recipient_id', recipientId)
      .order('created_at', { ascending: true })
    if (!error) setList(data || [])
    setLoading(false)
  }, [recipientId])

  useEffect(() => {
    load()
  }, [load])

  function startNew() {
    setForm(BLANK)
    setEditing('new')
  }
  function startEdit(s) {
    setForm({
      name: s.name || '', specialty: s.specialty || '', clinic_or_agency: s.clinic_or_agency || '',
      phone: s.phone || '', email: s.email || '', notes: s.notes || '',
    })
    setEditing(s.id)
  }

  async function save() {
    if (!form.name.trim()) {
      toast.error('Please enter a name.')
      return
    }
    setBusy(true)
    try {
      const payload = {
        care_recipient_id: recipientId,
        name: form.name.trim(),
        specialty: form.specialty.trim() || null,
        clinic_or_agency: form.clinic_or_agency.trim() || null,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        notes: form.notes.trim() || null,
      }
      const res =
        editing === 'new'
          ? await supabase.from('care_specialists').insert(payload)
          : await supabase.from('care_specialists').update(payload).eq('id', editing)
      if (res.error) throw res.error
      setEditing(null)
      await load()
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  async function remove(id) {
    if (!window.confirm('Remove this specialist?')) return
    const { error } = await supabase.from('care_specialists').delete().eq('id', id)
    if (error) return toast.error(humanizeError(error))
    setList((l) => l.filter((s) => s.id !== id))
  }

  return (
    <div className="card stack">
      <div className="spread">
        <div className="section-title">Specialists & care team</div>
        {canManage && editing === null && (
          <button className="btn btn-sm btn-secondary" onClick={startNew}>+ Add</button>
        )}
      </div>

      {loading ? (
        <p className="muted" style={{ fontSize: 14, margin: 0 }}>Loading…</p>
      ) : (
        <>
          {list.length === 0 && editing !== 'new' && (
            <p className="muted" style={{ fontSize: 14, margin: 0 }}>No specialists added yet.</p>
          )}

          {list.map((s) =>
            editing === s.id ? (
              <SpecialistForm key={s.id} form={form} setForm={setForm} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
            ) : (
              <div key={s.id} className="stack" style={{ gap: 2, paddingBottom: 10, borderBottom: '1px solid var(--border)' }}>
                <div className="spread">
                  <div style={{ fontWeight: 700 }}>{s.name}</div>
                  {canManage && (
                    <div className="row" style={{ gap: 10 }}>
                      <button onClick={() => startEdit(s)} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 13 }}>Edit</button>
                      <button onClick={() => remove(s.id)} style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13 }}>Remove</button>
                    </div>
                  )}
                </div>
                {s.specialty && <div className="muted" style={{ fontSize: 14 }}>{s.specialty}{s.clinic_or_agency ? ` · ${s.clinic_or_agency}` : ''}</div>}
                {s.phone && <a href={`tel:${s.phone}`} style={{ fontSize: 14 }}>{s.phone}</a>}
              </div>
            )
          )}

          {editing === 'new' && (
            <SpecialistForm form={form} setForm={setForm} onSave={save} onCancel={() => setEditing(null)} busy={busy} />
          )}
        </>
      )}
    </div>
  )
}

function SpecialistForm({ form, setForm, onSave, onCancel, busy }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))
  return (
    <div className="stack" style={{ gap: 8, padding: '10px 0' }}>
      <input className="input" placeholder="Name *" value={form.name} onChange={(e) => set('name', e.target.value)} />
      <input className="input" placeholder="Specialty (e.g. Neurologist)" value={form.specialty} onChange={(e) => set('specialty', e.target.value)} />
      <input className="input" placeholder="Clinic or agency" value={form.clinic_or_agency} onChange={(e) => set('clinic_or_agency', e.target.value)} />
      <div className="row" style={{ gap: 8 }}>
        <input className="input grow" placeholder="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
        <input className="input grow" placeholder="Email" value={form.email} onChange={(e) => set('email', e.target.value)} />
      </div>
      {/* notes was already saved by the payload but had no input to fill it. */}
      <textarea className="textarea" placeholder="Notes (optional)" value={form.notes} onChange={(e) => set('notes', e.target.value)} />
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn grow" onClick={onSave} disabled={busy}>{busy ? <span className="spinner" /> : 'Save'}</button>
      </div>
    </div>
  )
}
